<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink, type RouteLocationRaw } from 'vue-router';
import RayaIcon from './RayaIcon.vue';

const props = withDefaults(defineProps<{
  color?: 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info';
  variant?: 'flat' | 'tonal' | 'outlined' | 'text';
  size?: 'x-small' | 'small' | 'default' | 'large';
  loading?: boolean;
  disabled?: boolean;
  block?: boolean;
  prependIcon?: string;
  appendIcon?: string;
  icon?: string | boolean;
  type?: 'button'|'submit'|'reset';
  to?: RouteLocationRaw;
  href?: string;
  target?: string;
  rel?: string;
}>(), {
  color:'primary',
  variant:'flat',
  size:'default',
  type:'button',
  prependIcon:undefined,
  appendIcon:undefined,
  icon:undefined,
  to:undefined,
  href:undefined,
  target:undefined,
  rel:undefined,
});

const tag = computed(() => props.to ? RouterLink : props.href ? 'a' : 'button');
const iconOnly = computed(() => !!props.icon);
const resolvedRel = computed(() => props.rel ?? (props.target === '_blank' ? 'noopener noreferrer' : undefined));
const isDisabled = computed(() => Boolean(props.disabled || props.loading));

function guardDisabled(event: MouseEvent): void {
  if (!isDisabled.value) return;
  event.preventDefault();
  event.stopImmediatePropagation();
}
</script>

<template>
  <component
    :is="tag"
    :to="to"
    :href="href"
    :target="target"
    :rel="resolvedRel"
    :type="!to && !href ? type : undefined"
    :disabled="!to && !href ? isDisabled : undefined"
    :aria-disabled="isDisabled || undefined"
    :aria-busy="loading || undefined"
    :tabindex="isDisabled && (to || href) ? -1 : undefined"
    :class="[
      'raya-btn',
      `raya-btn--${variant}`,
      `raya-btn--${color}`,
      `raya-btn--${size}`,
      { 'raya-btn--block': block, 'raya-btn--icon': iconOnly, 'raya-btn--disabled': isDisabled },
    ]"
    @click.capture="guardDisabled"
  >
    <span
      v-if="loading"
      class="raya-btn__spinner"
      aria-hidden="true"
    />
    <RayaIcon
      v-else-if="prependIcon"
      :icon="prependIcon"
      size="18"
    />
    <RayaIcon
      v-if="typeof icon === 'string' && !loading"
      :icon="icon"
      size="18"
    />
    <span
      v-if="!iconOnly"
      class="raya-btn__content"
    ><slot /></span>
    <RayaIcon
      v-if="appendIcon && !loading"
      :icon="appendIcon"
      size="18"
    />
  </component>
</template>
